/**
 * Passkey Manager Component
 * Handles WebAuthn passkey registration and management
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { startRegistration } from '@simplewebauthn/browser';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import API_URL from '../../config/api';

const Container = styled.div``;

const SupportCheck = styled.div`
  padding: 1rem;
  background: ${props => props.$supported ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)'};
  border-radius: 8px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SupportIcon = styled.div`
  font-size: 1.5rem;
`;

const SupportText = styled.div`
  flex: 1;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const AddButton = styled.button`
  background: var(--accent-primary);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: var(--accent-secondary);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const PasskeyList = styled.div`
  display: grid;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const PasskeyCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: var(--bg-secondary);
  border-radius: 8px;
  border: 2px solid var(--border-color);
`;

const PasskeyInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
`;

const PasskeyIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--accent-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
`;

const PasskeyDetails = styled.div`
  flex: 1;
`;

const PasskeyName = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const PasskeyMeta = styled.div`
  font-size: 0.85rem;
  color: var(--text-primary);
  opacity: 0.6;
`;

const DeleteButton = styled.button`
  background: transparent;
  color: #f44336;
  border: 2px solid #f44336;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #f44336;
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-primary);
  opacity: 0.6;
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const EmptyText = styled.div`
  font-size: 1.1rem;
`;

const InfoBox = styled.div`
  background: rgba(33, 150, 243, 0.1);
  border-left: 4px solid #2196F3;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
`;

const InfoTitle = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const InfoText = styled.div`
  font-size: 0.9rem;
  color: var(--text-primary);
  opacity: 0.8;
  line-height: 1.6;
`;

function PasskeyManager({ passkeys, onPasskeysChange }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [localPasskeys, setLocalPasskeys] = useState(passkeys || []);

  // Check if WebAuthn is supported
  const isWebAuthnSupported = window?.PublicKeyCredential !== undefined;

  // Fetch passkeys on mount
  React.useEffect(() => {
    const fetchPasskeys = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const credentials = data.user?.webauthnCredentials || [];
          // Map webauthnCredentials to match expected format
          const formattedPasskeys = credentials.map(c => ({
            id: c._id,
            name: c.friendlyName,
            createdAt: c.createdAt,
            lastUsed: c.lastUsed
          }));
          setLocalPasskeys(formattedPasskeys);
          onPasskeysChange(formattedPasskeys.length);
        }
      } catch (error) {
        console.error('Error fetching passkeys:', error);
      }
    };

    fetchPasskeys();
  }, [token, onPasskeysChange]);

  const handleAddPasskey = async () => {
    if (!isWebAuthnSupported) {
      toast.error('Your browser does not support passkeys');
      return;
    }

    try {
      setLoading(true);

      // Get registration options from server
      const optionsResponse = await fetch(
        `${API_URL}/api/auth/passkey/register-options`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!optionsResponse.ok) {
        const error = await optionsResponse.json();
        throw new Error(error.message || 'Failed to get registration options');
      }

      const responseData = await optionsResponse.json();
      
      // Server now sends options directly (not wrapped in { success, options })
      // This is the preferred format for @simplewebauthn/browser
      let attResp;
      try {
        attResp = await startRegistration(responseData);
      } catch (error) {
        console.error('WebAuthn registration error:', error);
        if (error.name === 'NotAllowedError') {
          throw new Error('Registration was cancelled');
        }
        throw new Error('Failed to create passkey. Please try again.');
      }

      // Verify registration with server
      const verifyResponse = await fetch(
        `${API_URL}/api/auth/passkey/register-verify`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            credential: attResp,
            friendlyName: `${navigator.userAgent.includes('Chrome') ? 'Chrome' : navigator.userAgent.includes('Safari') ? 'Safari' : 'Browser'} on ${navigator.platform}`
          })
        }
      );

      if (!verifyResponse.ok) {
        const error = await verifyResponse.json();
        throw new Error(error.message || 'Failed to verify passkey');
      }

      const result = await verifyResponse.json();
      
      // Update local passkey list - server returns all credentials
      const updatedPasskeys = result.credentials || [];
      setLocalPasskeys(updatedPasskeys);
      onPasskeysChange(updatedPasskeys.length);

      toast.success('Passkey added successfully!');
    } catch (error) {
      console.error('Passkey registration error:', error);
      toast.error(error.message || 'Failed to add passkey');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePasskey = async (passkeyId) => {
    if (!window.confirm('Are you sure you want to delete this passkey?')) {
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(
        `${API_URL}/api/auth/passkey/${passkeyId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete passkey');
      }

      // Update local passkey list
      const updatedPasskeys = localPasskeys.filter(pk => pk.id !== passkeyId);
      setLocalPasskeys(updatedPasskeys);
      onPasskeysChange(updatedPasskeys.length);

      toast.success('Passkey deleted successfully');
    } catch (error) {
      console.error('Passkey deletion error:', error);
      toast.error(error.message || 'Failed to delete passkey');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDeviceIcon = (name) => {
    const nameLower = name?.toLowerCase() || '';
    if (nameLower.includes('chrome')) return '🌐';
    if (nameLower.includes('safari')) return '🧭';
    if (nameLower.includes('edge')) return '🌊';
    if (nameLower.includes('firefox')) return '🦊';
    if (nameLower.includes('windows')) return '🪟';
    if (nameLower.includes('mac')) return '🍎';
    if (nameLower.includes('iphone') || nameLower.includes('ios')) return '📱';
    if (nameLower.includes('android')) return '🤖';
    return '🔑';
  };

  return (
    <Container>
      {!isWebAuthnSupported && (
        <SupportCheck $supported={false}>
          <SupportIcon>❌</SupportIcon>
          <SupportText>
            Your browser does not support passkeys. Please use Chrome, Safari, Edge, or another
            modern browser with WebAuthn support.
          </SupportText>
        </SupportCheck>
      )}

      {isWebAuthnSupported && (
        <SupportCheck $supported={true}>
          <SupportIcon>✅</SupportIcon>
          <SupportText>
            Your browser supports passkeys! You can use Face ID, Touch ID, Windows Hello,
            or hardware security keys to sign in.
          </SupportText>
        </SupportCheck>
      )}

      <InfoBox>
        <InfoTitle>What are Passkeys?</InfoTitle>
        <InfoText>
          Passkeys are a safer and easier alternative to passwords. They use your device's
          built-in biometric authentication (like Face ID or fingerprint) or a security key
          to sign you in. Passkeys can't be phished and are unique to this site.
        </InfoText>
      </InfoBox>

      <AddButton onClick={handleAddPasskey} disabled={!isWebAuthnSupported || loading}>
        <span>➕</span>
        {loading ? 'Adding Passkey...' : 'Add Passkey'}
      </AddButton>

      {localPasskeys.length > 0 ? (
        <PasskeyList>
          {localPasskeys.map((passkey) => (
            <PasskeyCard key={passkey.id}>
              <PasskeyInfo>
                <PasskeyIcon>
                  {getDeviceIcon(passkey.name)}
                </PasskeyIcon>
                <PasskeyDetails>
                  <PasskeyName>
                    {passkey.name || 'Unnamed Device'}
                  </PasskeyName>
                  <PasskeyMeta>
                    Added {formatDate(passkey.createdAt)}
                    {passkey.lastUsed && ` • Last used ${formatDate(passkey.lastUsed)}`}
                  </PasskeyMeta>
                </PasskeyDetails>
              </PasskeyInfo>
              <DeleteButton
                onClick={() => handleDeletePasskey(passkey.id)}
                disabled={loading}
              >
                Delete
              </DeleteButton>
            </PasskeyCard>
          ))}
        </PasskeyList>
      ) : (
        <EmptyState>
          <EmptyIcon>🔑</EmptyIcon>
          <EmptyText>
            No passkeys configured. Add one to enable passwordless sign-in!
          </EmptyText>
        </EmptyState>
      )}
    </Container>
  );
}

export default PasskeyManager;
